import type { FC } from 'react';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Image from '../../../../shared/components/Image';
import {
  FavoriteOnIcon,
  FavoriteOffIcon,
} from '../../../games/components/GameCard/components/ButtonFavorites/FavoriteIcon';
import TagsContainer from '../../../games/components/GameCard/components/GameComponents/TagsContainer';
import ButtonPlay from '../../../games/components/GameCard/components/ButtonPlay/ButtonPlay';
import { useFavoritesStore, useIsFavorite } from '../../../favorites/store/favoritesStore';
import { useGameStartActions, usePendingGameKey } from '../../../games/hooks/useGameStart';
import { useJackpotNameByGameKey } from 'features/jackpots';
import { getBadgeCSSVars, useEffectiveGameData } from 'features/games';
import { SECTION_NAMES } from '@repo/common';
import { useGameSounds } from '../../../../shared/hooks/useSound';
import { clickCounter } from '../../../../core/services/EventTracker';
import { transactionTracker } from '../../../../core/services/TransactionTracker';
import type { ITopWin } from '../../types/topWins.types';
import { useTimeAgo } from '../../hooks/useTimeAgo';
import TopWinAmount from '../TopWinAmount';
import { findGameByKey, useGameByKey, buildGameStub } from '../../utils/gameCatalog';
import { deriveJackpotBadge } from '../../utils/jackpotBadge';
import styles from './TopWinFeaturedCard.module.css';

interface TopWinFeaturedCardProps {
  win: ITopWin;
  /**
   * 'main' (default) is the multi-view featured card and the first card of the
   * single-view list: hero-yellow amount + yellow glow. 'plain' is every other
   * card in the single-view list: white amount, no glow.
   */
  variant?: 'main' | 'plain';
  /** Tracking origin — distinguishes multi-view vs the single-view tab. */
  view?: 'multi' | 'single';
}

const TopWinFeaturedCard: FC<TopWinFeaturedCardProps> = ({ win, variant = 'main', view = 'multi' }) => {
  const { t } = useTranslation();
  const timeAgo = useTimeAgo(win.wonAt);
  const isFavorite = useIsFavorite(win.gameKey);

  // Some jackpot-carrying games are missing from the portal catalog; the jackpots store still
  // knows their instance, so the stub borrows it. Premium Link resolution is left entirely to
  // useEffectiveGameData — the same path the normal game card takes.
  const game = useGameByKey(win.gameKey);
  const resolvedJackpotName = useJackpotNameByGameKey(win.gameKey);
  const gameData = useMemo(
    () => game ?? { ...buildGameStub(win), jackpotName: resolvedJackpotName ?? null },
    [game, win, resolvedJackpotName],
  );

  const { effectiveData, effectiveSectionName } = useEffectiveGameData(gameData, SECTION_NAMES.TOP_WIN_GAMES);
  const jackpotBadge = useMemo(
    () => deriveJackpotBadge(effectiveData, effectiveSectionName),
    [effectiveData, effectiveSectionName],
  );

  // Only Premium Link needs the per-section badge vars; anything else keeps the CSS fallbacks
  // (DEFAULT_BADGE_CONFIG would narrow the SPJ badge from 40% to 36% above 769px).
  const badgeCSSVars =
    jackpotBadge?.sectionName === SECTION_NAMES.PREMIUM_LINK ? getBadgeCSSVars(SECTION_NAMES.PREMIUM_LINK) : undefined;

  const { startGame } = useGameStartActions();
  const { playSelectGameSound } = useGameSounds();
  const pendingGameKey = usePendingGameKey();
  const isLoading = pendingGameKey === win.gameKey;

  const handleFavoriteClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      const favoriteGame = findGameByKey(win.gameKey) ?? buildGameStub(win);
      useFavoritesStore.getState().toggleFavorite(win.gameKey, favoriteGame);
    },
    [win],
  );

  const handleClick = useCallback(() => {
    if (isLoading) return;
    clickCounter.incrementClick(`click_game_${win.gameKey}_${view}_top_wins`);
    transactionTracker.trackGameOpen(win.gameName, win.gameKey, 'TOP_WIN_GAMES', view);
    playSelectGameSound();
    startGame(win.gameKey);
  }, [isLoading, win.gameKey, win.gameName, view, startGame, playSelectGameSound]);

  return (
    <div
      className={styles.card}
      style={badgeCSSVars}
      onClick={handleClick}
      data-loading={isLoading || undefined}
      data-variant={variant}
      data-view={view}
    >
      <Image src={`/verticals/${win.gameKey}`} alt={win.gameName} className={styles.thumbnail} />
      <div className={styles.overlay} aria-hidden="true" />

      {jackpotBadge && (
        <>
          {jackpotBadge.showPlate && (
            <Image
              src={`/badges/${jackpotBadge.fileName}_plate`}
              alt={jackpotBadge.label}
              loading="lazy"
              className={`badge_plate badge_plate_${jackpotBadge.slug} ${styles.badgePlate}`}
            />
          )}
          <TagsContainer isSPJjackpot={jackpotBadge.isSPJ}>
            <Image
              src={`/badges/${jackpotBadge.fileName}`}
              alt={jackpotBadge.label}
              loading="lazy"
              className={`badge badge_${jackpotBadge.slug}`}
            />
          </TagsContainer>
        </>
      )}

      <div
        role="button"
        className={styles.favoriteButton}
        onClick={handleFavoriteClick}
        aria-label={
          isFavorite ? t('favourites.remove', 'Remove from favourites') : t('favourites.add', 'Add to favourites')
        }
        aria-pressed={isFavorite}
      >
        {isFavorite ? <FavoriteOnIcon /> : <FavoriteOffIcon />}
      </div>

      <div className={styles.footer}>
        <TopWinAmount win={win} />
        <span className={styles.timeAgo}>{timeAgo}</span>
      </div>

      <ButtonPlay onClick={handleClick} className={`egt_game_btn_play ${styles.playButton}`} />
    </div>
  );
};

export default memo(TopWinFeaturedCard);
